import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { useNoIndex } from "../hooks/useNoIndex";
import { EmailVerifyBanner } from "../components/EmailVerifyBanner";
import { Flame, Trophy, BookOpen, MessageCircle, ArrowRight, Check, Brain } from "lucide-react";

const DARUMA = "https://static.prod-images.emergentagent.com/jobs/6aac7739-0d83-4999-87fa-93aae3a1400b/images/14f34c12bb71d430ec756ab3328fec1b2292cdadf2dee1d9d4f80344385b08a3.png";

const Dashboard = () => {
  useNoIndex();
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    api.get("/lessons").then((r) => setLessons(r.data.data)).catch(() => {});
  }, []);

  if (!user) return null;
  const done = user.completed_lessons || [];
  const progress = lessons.length ? Math.round((done.length / lessons.length) * 100) : 0;

  return (
    <div className="washi-bg min-h-screen" data-testid="dashboard-page">
      <Navbar />
      <EmailVerifyBanner />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        {/* Hello strip */}
        <div className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-8 bg-[#1A2A40] text-white rounded-3xl p-8 sm:p-10 shadow-brut border-2 border-[#1A2A40] relative overflow-hidden">
            <div className="absolute -right-4 -bottom-4 w-40 h-40 opacity-90">
              <img src={DARUMA} alt="" className="w-full h-full object-contain" />
            </div>
            <div className="relative max-w-md">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFD166] mb-2">Bienvenue</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] mb-3">
                {user.name},<br /><span className="font-jp text-[#E94A3F]">頑張ってね</span>.
              </h1>
              <p className="text-white/80">Niveau actuel : <span className="font-bold text-[#FFD166]">{user.level}</span> · {user.xp} XP gagnés.</p>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4">
            <StatCard icon={<Flame className="w-5 h-5" />} k={`${user.streak} j`} v="Série en cours" c="#E94A3F" testid="stat-streak" />
            <StatCard icon={<Trophy className="w-5 h-5" />} k={`${done.length}/${lessons.length}`} v="Leçons" c="#8CA07A" testid="stat-lessons" />
          </div>
        </div>

        {/* Progress */}
        <div className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-10" data-testid="progress-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black text-2xl text-[#1A2A40]">Votre parcours</h2>
            <span className="font-display font-black text-3xl text-[#E94A3F]">{progress}%</span>
          </div>
          <div className="h-3 bg-[#F3F6F1] rounded-full overflow-hidden">
            <div className="h-full bg-[#E94A3F] rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <QuickCard to="/kana" icon={<BookOpen />} title="Kana" subtitle="46 + 46 caractères" color="#FFD166" testid="quick-kana" />
          <QuickCard to="/review" icon={<Brain />} title="Réviser" subtitle="Mémorisation espacée" color="#8CA07A" testid="quick-review" light />
          <QuickCard to="/jlpt" icon={<Trophy />} title="JLPT blanc" subtitle="Examen chronométré" color="#1A2A40" testid="quick-jlpt" light />
          <QuickCard to="/tutor" icon={<MessageCircle />} title="Sensei IA" subtitle="Tuteur 24/7" color="#E94A3F" testid="quick-tutor" light />
        </div>

        {/* Lessons */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-black text-3xl text-[#1A2A40]">Leçons</h2>
            <Link to="/lessons" className="text-sm font-bold text-[#E94A3F] hover:underline inline-flex items-center gap-1">
              Tout voir <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lessons.slice(0, 6).map((l) => {
              const isDone = done.includes(l.id);
              return (
                <Link
                  key={l.id} to={`/lessons/${l.id}`}
                  data-testid={`lesson-card-${l.id}`}
                  className={`group rounded-2xl p-6 border-2 hover-lift block ${isDone ? "bg-[#8CA07A]/10 border-[#8CA07A]" : "bg-[#FDFBF7] border-[#1A2A40]/10"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-4xl">{l.emoji}</div>
                    {isDone && <Check className="w-5 h-5 text-[#8CA07A]" />}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#E94A3F] mb-1">{l.level} · {l.duration}</div>
                  <div className="font-display font-bold text-lg text-[#1A2A40] mb-1 group-hover:text-[#E94A3F] transition-colors">{l.title}</div>
                  <div className="text-sm text-[#4A5568]">{l.subtitle}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const StatCard = ({ icon, k, v, c, testid }) => (
  <div className="bg-[#FDFBF7] rounded-2xl p-5 border-2 border-[#1A2A40]/10" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${c}33`, color: c }}>{icon}</div>
    <div className="font-display font-black text-2xl text-[#1A2A40]">{k}</div>
    <div className="text-xs uppercase tracking-wider text-[#4A5568]">{v}</div>
  </div>
);

const QuickCard = ({ to, icon, title, subtitle, color, testid, light }) => (
  <Link
    to={to}
    data-testid={testid}
    className="rounded-3xl p-6 border-2 border-[#1A2A40] shadow-brut-sm hover-lift block"
    style={{ background: color, color: light ? "white" : "#1A2A40" }}
  >
    <div className="w-10 h-10 mb-4">{icon}</div>
    <div className="font-display font-bold text-xl mb-1">{title}</div>
    <div className="text-sm opacity-80">{subtitle}</div>
  </Link>
);

export default Dashboard;
